import React, { useContext, useState } from "react";
import { t } from "ttag";

import type {
    ServerCmsTypesProductSummary as ProductSummary,
    PDPHeroBlock as Value,
} from "@reactivated";

import type { HeroLayoutSettings } from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";

import { ReactivatedContext as Context } from "../../context";
import type { Favorite } from "../../favorites";
import { ButtonPrimary, ButtonSecondary } from "../Clickables";
import { FavoriteToggleButton } from "../FavoriteToggleButton";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { ProductStatusBadge } from "../ProductStatusBadge";
import { Svg } from "../Svg";
import { QuoteSlider } from "./QuoteSlider";

import themeStyles from "../../styles/themes-2026.module.scss";
import styles from "./index.module.scss";

export interface Props {
    value: Value;
    sku: string;
    title: string;
    status: ProductSummary["status"];
}

export const PDPHeroBlock = ({ value, sku, title, status }: Props) => {
    const context = useContext(Context);
    const product: Favorite = { name: title, sku, url: context.request.url };
    const [quoteOpen, setQuoteOpen] = useState(false);
    const isDiscontinued = status === "discontinued";
    const showActions = !isDiscontinued;

    const content = (
        <>
            <ProductStatusBadge status={status} />
            <p className={styles.sku}>{sku}</p>
            <h1 className={styles.title}>{title}</h1>
            {showActions && (
                <div className={styles.actions}>
                    <ButtonSecondary onPress={() => setQuoteOpen(true)}>
                        {t`Request a Quote`}
                    </ButtonSecondary>
                    <ButtonPrimary
                        className={styles.showroomButton}
                        href={context.wagtail_links.find_dealer}
                    >
                        {t`Find a Showroom`}
                        <Svg name="caret-right" />
                    </ButtonPrimary>
                </div>
            )}
            <FavoriteToggleButton
                product={product}
                textVisible
                className={styles.favoritesToggle}
                svgClassName={styles.favoritesToggleSvg}
            />
        </>
    );

    const media =
        value.media && value.media.length > 0 ? (
            <MediaChooserBlock value={value.media} />
        ) : null;

    // Mobile-specific art-direction; falls back to the desktop media when unset.
    const mobileMedia =
        value.mobile_media && value.mobile_media.length > 0 ? (
            <MediaChooserBlock value={value.mobile_media} />
        ) : (
            media
        );

    const sharedStyles = {
        rootClassName: themeStyles.eggplantGradient,
        innerClassName: styles.inner,
        mediaClassName: styles.media,
        contentClassName: styles.content,
    };

    const settings: HeroLayoutSettings = {
        heroDirection: "row",
        heroPlacement: "backgroundFull",
        mediaPlacement: "background",
        mediaAspectRatio: "stretch",
        contentAlignPrimary: "middle",
        contentAlignSecondary: "right/bottom",
        textAlignment: "left",
        contentWidth: 4,
        minHeight: 400,
    };

    const baseLayout = {
        content,
        media,
        settings,
        styles: sharedStyles,
    };

    return (
        <>
            <HeroSet
                layouts={{
                    desktop: baseLayout,
                    laptop: {
                        ...baseLayout,
                        settings: {
                            ...settings,
                            contentWidth: 5,
                        },
                    },
                    tablet: {
                        ...baseLayout,
                        settings: {
                            ...settings,
                            contentWidth: 6,
                        },
                    },
                    mobile: {
                        ...baseLayout,
                        media: mobileMedia,
                        settings: {
                            ...settings,
                            contentAlignPrimary: "left/top",
                            contentWidth: 12,
                        },
                    },
                }}
            />
            {showActions && (
                <QuoteSlider
                    title={title}
                    isOpen={quoteOpen}
                    onClose={() => setQuoteOpen(false)}
                />
            )}
        </>
    );
};
